import React, { useCallback, useRef, useState } from 'react';
import MainBlock from './smallcomponents/MainBlock';
import { useSurveyStore } from '../surveyutilities/surveyStore';

const QUICK_ADD = [
    { key: 'text', icon: 'bi-input-cursor-text', title: 'Single-line text' },
    { key: 'textarea', icon: 'bi-textarea-t', title: 'Multi-line text' },
    { key: 'single_choice', icon: 'bi-ui-radios', title: 'Radio group' },
    { key: 'multiple_choice', icon: 'bi-ui-checks', title: 'Checkbox' },
    { key: 'dropdown', icon: 'bi-menu-button-wide', title: 'Dropdown' },
    { key: 'rating', icon: 'bi-star', title: 'Rating' },
];

function EcxSurveyCanvas() {
    const questions = useSurveyStore((state) => state.questions);
    const survey = useSurveyStore((state) => state.survey);
    const setSurveySetting = useSurveyStore((state) => state.setSurveySetting);
    const addQuestion = useSurveyStore((state) => state.addQuestion);
    const reorderQuestion = useSurveyStore((state) => state.reorderQuestion);
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const draggingIdRef = useRef(null);

    const clearDragState = useCallback(() => {
        draggingIdRef.current = null;
        setDraggingId(null);
        setDragOverId(null);
    }, []);

    const handleDragStart = useCallback((event, questionId) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', questionId);
        draggingIdRef.current = questionId;
        setDraggingId(questionId);
        setDragOverId(questionId);
    }, []);

    const handleDragOver = useCallback((event, questionId) => {
        if (!draggingIdRef.current) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragOverId((current) => (current === questionId ? current : questionId));
    }, []);

    const handleDrop = useCallback(
        (event, targetId) => {
            event.preventDefault();
            const sourceId = draggingIdRef.current || event.dataTransfer.getData('text/plain');
            if (sourceId && targetId && sourceId !== targetId) {
                reorderQuestion(sourceId, targetId);
            }
            clearDragState();
        },
        [clearDragState, reorderQuestion]
    );

    const handleDragEnd = useCallback(() => {
        clearDragState();
    }, [clearDragState]);

    return (
        <div className="ecx-survey-editor__canvas-scroll">
            <div className="ecx-survey-editor__canvas-paper">
                <div className="ecx-survey-canvas__header">
                    {editingTitle ? (
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            autoFocus
                            value={survey?.title || ''}
                            onChange={(e) => setSurveySetting('title', e.target.value)}
                            onBlur={() => setEditingTitle(false)}
                            placeholder="Survey title"
                        />
                    ) : (
                        <button
                            type="button"
                            className="ecx-survey-canvas__title"
                            onClick={() => setEditingTitle(true)}
                        >
                            {survey?.title || 'Untitled survey'}
                        </button>
                    )}

                    {editingDescription ? (
                        <textarea
                            className="form-control form-control-sm"
                            rows={2}
                            autoFocus
                            value={survey?.description || ''}
                            onChange={(e) => setSurveySetting('description', e.target.value)}
                            onBlur={() => setEditingDescription(false)}
                            placeholder="Survey description"
                        />
                    ) : (
                        <button
                            type="button"
                            className="ecx-survey-canvas__desc"
                            onClick={() => setEditingDescription(true)}
                        >
                            {survey?.description || 'Add a survey description…'}
                        </button>
                    )}
                </div>

                {!questions || questions.length === 0 ? (
                    <div className="ecx-survey-editor__empty">
                        <span className="ecx-survey-editor__empty-icon">
                            <i className="bi bi-clipboard-check" />
                        </span>
                        <div className="fw-normal text-dark">No questions yet</div>
                        <div className="small">
                            Use the toolbar or the buttons below to add your first question.
                        </div>
                    </div>
                ) : (
                    <div
                        className={`ecx-survey-canvas__list${draggingId ? ' is-reordering' : ''}`}
                    >
                        {questions.map((block, index) => (
                            <MainBlock
                                key={block.internalId}
                                block={block}
                                index={index}
                                totalBlocks={questions.length}
                                isDragging={draggingId === block.internalId}
                                isDragOver={
                                    Boolean(draggingId) &&
                                    dragOverId === block.internalId &&
                                    draggingId !== block.internalId
                                }
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                            />
                        ))}
                    </div>
                )}

                <div className="ecx-survey-editor__add-row">
                    <div className="ecx-survey-editor__add-label">Add question</div>
                    <div className="ecx-survey-editor__add-group">
                        {QUICK_ADD.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className="ecx-survey-editor__add-btn"
                                title={item.title}
                                onClick={() => addQuestion(item.key)}
                            >
                                <i className={`bi ${item.icon}`} />
                                <span>{item.title}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EcxSurveyCanvas;
